import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/components/layout/Page';
import { PageContent } from '@/components/layout/PageContent';
import { JOIN_RACE_BY_CODE } from '@/graphql/mutations';
import { extractErrorCode } from '@/lib/graphql-errors';

const JOIN_ERROR_KEYS: Record<string, string> = {
    NOT_FOUND: 'quickActions.joinError.notFound',
    VALIDATION_ERROR: 'quickActions.joinError.notPrivate',
    CONFLICT: 'quickActions.joinError.alreadyJoined',
};

export function JoinByCodePage() {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation('home');
    const [joinRace] = useMutation(JOIN_RACE_BY_CODE);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const attempted = useRef(false);

    useEffect(() => {
        if (!code || attempted.current) return;
        attempted.current = true;

        joinRace({ variables: { code } })
            .then(({ data }) => {
                const race = data?.joinRaceByCode;
                if (race) {
                    navigate(`/races/${race.id}`, { replace: true });
                }
            })
            .catch((err: unknown) => {
                const errCode = extractErrorCode(err);
                const messageKey = JOIN_ERROR_KEYS[errCode] ?? 'quickActions.joinError.unknown';
                setError(t(messageKey));
                setErrorCode(errCode);
            });
    }, [code, joinRace, navigate, t]);

    if (error) {
        return (
            <Page>
                <PageContent className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                    <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center">
                        <AlertCircle size={28} className="text-red-500" />
                    </div>
                    <p className="text-slate-700 font-medium text-center">{error}</p>
                    <div className="flex items-center gap-3 mt-2">
                        <button
                            onClick={() => navigate('/', { replace: true })}
                            className="px-6 py-2.5 rounded-xl font-bold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors"
                        >
                            {t('quickActions.joinError.backHome', 'Back to home')}
                        </button>
                        {errorCode === 'CONFLICT' && code && (
                            <button
                                onClick={() => navigate(`/races`, { replace: true })}
                                className="px-6 py-2.5 rounded-xl font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                            >
                                {t('quickActions.joinError.viewRaces', 'Browse races')}
                            </button>
                        )}
                    </div>
                </PageContent>
            </Page>
        );
    }

    return (
        <Page>
            <PageContent className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-emerald-600" size={32} />
            </PageContent>
        </Page>
    );
}
